import React, { useEffect, useState } from "react";
import {
  X,
  Eye,
  FileText,
  Calendar,
  User,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
  Image,
  Video,
  Paperclip,
  Download,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { FUNCTION_NAMES } from "../constants";
import { API_BASE_URL } from "../config";
import { fetchWithRetry } from "../utils/tourUtils";
import TourViewer from "./TourViewer";

// Attachment Viewer Component
const AttachmentViewer = ({ attachment, onImageMagnify }) => {
  const [presignedUrl, setPresignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generatePresignedUrl = async (s3Path, isVideo = false) => {
    try {
      setLoading(true);

      // Use GET_VIDEO_H264 for video files to ensure H.264 compatibility
      const functionName = FUNCTION_NAMES.POST.GET_PRESIGNED_URL;

      const response = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: functionName,
          payload: {
            s3Key: s3Path,
            expiresIn: 3600,
          },
        },
        label: isVideo ? "get H.264 video" : "generate presigned URL",
      });

      if (isVideo && response.converted) {
        console.log(`✅ Video converted to H.264: ${response.h264S3Key}`);
      }
      console.log("link", response.url);
      return response.url;
    } catch (err) {
      console.error("Error generating presigned URL:", err);
      setError("Failed to load attachment");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (attachment.s3Path && !presignedUrl) {
      const fileExtension = getFileExtension(attachment.filePath);
      const isVideo =
        attachment.attachmentType === "VIDEO" ||
        ["mp4", "webm", "ogg", "avi", "mov", "m4v", "3gp", "mkv"].includes(
          fileExtension,
        );
      generatePresignedUrl(attachment.s3Path, isVideo).then(setPresignedUrl);
    }
  }, [
    attachment.s3Path,
    attachment.filePath,
    attachment.attachmentType,
    presignedUrl,
  ]);

  const getFileExtension = (filePath) => {
    return filePath?.split(".").pop()?.toLowerCase() || "";
  };

  const getAttachmentIcon = (type) => {
    switch (type) {
      case "IMAGE360":
      case "TABLETIMAGE":
        return <Image className="w-4 h-4" />;
      case "VIDEO":
        return <Video className="w-4 h-4" />;
      default:
        return <Paperclip className="w-4 h-4" />;
    }
  };

  const renderAttachment = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-4 bg-gray-100 rounded">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600">Loading...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center p-4 bg-red-50 rounded text-red-600">
          <span className="text-sm">{error}</span>
        </div>
      );
    }

    if (!presignedUrl) return null;

    const fileExtension = getFileExtension(attachment.filePath);

    // Debug logging to understand file type detection
    console.log("Attachment debug info:", {
      filePath: attachment.filePath,
      fileExtension: fileExtension,
      attachmentType: attachment.attachmentType,
      s3Path: attachment.s3Path,
    });

    // Check if it's a video file
    const isVideo =
      attachment.attachmentType === "VIDEO" ||
      ["mp4", "webm", "ogg", "avi", "mov", "m4v", "3gp", "mkv"].includes(
        fileExtension,
      );

    console.log("Is video file?", isVideo);

    // Image attachments
    if (
      attachment.attachmentType === "IMAGE360" ||
      attachment.attachmentType === "TABLETIMAGE" ||
      ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(fileExtension)
    ) {
      return (
        <div className="relative group">
          <img
            src={presignedUrl}
            alt="Attachment"
            className="max-w-full max-h-64 object-contain rounded border cursor-pointer"
            onError={() => setError("Failed to load image")}
            onClick={() =>
              onImageMagnify && onImageMagnify(presignedUrl, attachment)
            }
          />
          {/* Magnify button overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded border flex items-center justify-center">
            <Button
              variant="secondary"
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={(e) => {
                e.stopPropagation();
                onImageMagnify && onImageMagnify(presignedUrl, attachment);
              }}
            >
              <ZoomIn className="w-4 h-4 mr-1" />
              הגדל
            </Button>
          </div>
        </div>
      );
    }

    // Video attachments
    if (isVideo) {
      return (
        <div className="w-full bg-black rounded border overflow-hidden">
          <video
            controls
            width="100%"
            height="auto"
            style={{
              minHeight: "300px",
              maxHeight: "600px",
              objectFit: "contain",
              backgroundColor: "#000",
              aspectRatio: "4/3",
            }}
            onError={(e) => {
              console.error("Video error:", e.target.error);
              setError(
                `Failed to load video: ${e.target.error?.message || "Unknown error"}`,
              );
            }}
            onLoadedMetadata={(e) => {
              console.log("Video loaded:", {
                width: e.target.videoWidth,
                height: e.target.videoHeight,
                duration: e.target.duration,
              });
            }}
            playsInline
          >
            <source src={presignedUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    // Other file types - show download option
    return (
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded border">
        <div className="flex items-center gap-2">
          {getAttachmentIcon(attachment.attachmentType)}
          <span className="text-sm font-medium">
            {attachment.filePath?.split("/").pop()}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => window.open(presignedUrl, "_blank")}
        >
          <Download className="w-3 h-3 mr-1" />
          Download
        </Button>
      </div>
    );
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {getAttachmentIcon(attachment.attachmentType)}
          <span className="text-xs text-gray-600">
            {new Date(attachment.captureDate).toLocaleDateString("he-IL")} •{" "}
            {attachment.attachmentType}
          </span>
          <Badge
            variant={
              attachment.uploadStatus === "UPLOADED" ? "default" : "secondary"
            }
            className="text-xs"
          >
            {attachment.uploadStatus}
          </Badge>
        </div>
        {/* Download button moved to header */}
        {presignedUrl && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2"
            onClick={() => window.open(presignedUrl, "_blank")}
          >
            <Download className="w-3 h-3" />
          </Button>
        )}
      </div>
      {renderAttachment()}
    </div>
  );
};

// Helper functions for push queue data
const getLatestPushQueueItem = (pushQueueItems) => {
  if (!pushQueueItems || pushQueueItems.length === 0) return null;
  return pushQueueItems.reduce((latest, current) =>
    new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest,
  );
};

const getPushQueueStatusColor = (pushItem) => {
  if (pushItem.approve) return "bg-green-100 text-green-800";
  if (pushItem.reject) return "bg-red-100 text-red-800";
  switch (pushItem.pushStatus) {
    case "PENDING":
      return "bg-yellow-100 text-yellow-800";
    case "PUSHING":
      return "bg-blue-100 text-blue-800";
    case "PUSHED":
      return "bg-green-100 text-green-800";
    case "FAILED":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getPushQueueStatusText = (pushItem) => {
  if (pushItem.approve) return "מאושר";
  if (pushItem.reject) return "נדחה";
  switch (pushItem.pushStatus) {
    case "PENDING":
      return "ממתין";
    case "PUSHING":
      return "מעדכן";
    case "PUSHED":
      return "עודכן";
    case "FAILED":
      return "כשל";
    default:
      return pushItem.pushStatus;
  }
};

const getChangeTypeText = (changeType) => {
  const typeMap = {
    ITEM_UPDATE: "עדכון פריט",
    ATTACHMENT_ADDED: "קובץ נוסף",
    APPROVAL_CHANGED: "שינוי אישור",
    REMARK_ADDED: "הערה נוספה",
  };
  return typeMap[changeType] || changeType;
};

// Helper function to get user's full name
const getUserFullName = (userId, users) => {
  const user = users[userId];
  if (!user) return "משתמש לא ידוע";

  // Prefer Hebrew names, fall back to English if needed
  const firstName = user.firstNameHe || user.firstNameEn || "";
  const lastName = user.lastNameHe || user.lastNameEn || "";

  return `${firstName} ${lastName}`.trim() || user.email || "משתמש לא ידוע";
};

// Helper function to get contractor's name from checklist relationship
const getContractorNameFromChecklist = (checklist) => {
  // Get contractor from the relationship
  const contractorRelationship = checklist?.contractorOnSpacesChecklists?.[0];
  const contractor = contractorRelationship?.contractor;

  if (!contractor) return "קבלן לא ידוע";

  // Prefer Hebrew name, fall back to English if needed
  return contractor.nameHe || contractor.nameEn || "קבלן לא ידוע";
};

// Helper function to get contractor's name from tour relationship
const getContractorNameFromTour = (tour) => {
  // Get contractor from the ContractorOnTour relationship
  const contractorRelationship = tour?.contractors?.[0];
  const contractor = contractorRelationship?.contractor;

  if (!contractor) return null;

  // Prefer Hebrew name, fall back to English if needed
  return contractor.nameHe || contractor.nameEn || "קבלן לא ידוע";
};

// Expandable Checklist Item Component
const ExpandableChecklistItem = ({
  item,
  onImageMagnify,
  users = {},
  checklist = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLogHistoryExpanded, setIsLogHistoryExpanded] = useState(false);
  const hasAttachments = item.attachments && item.attachments.length > 0;
  const hasPushQueue = item.pushQueueItems && item.pushQueueItems.length > 0;
  const latestPushItem = getLatestPushQueueItem(item.pushQueueItems);

  // Determine background color based on approval status
  const getCardBackground = () => {
    if (latestPushItem?.approve) return "bg-green-50 border-green-200";
    if (latestPushItem?.reject) return "bg-red-50 border-red-200";
    return "";
  };

  return (
    <Card className={getCardBackground()}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h5 className="font-medium">{item.ChecklistItem?.title}</h5>
              {(hasAttachments ||
                (hasPushQueue && item.pushQueueItems.length > 1)) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-6 w-6 p-0"
                >
                  {isExpanded ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {item.ChecklistItem?.description}
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>סוג: {item.ChecklistItem?.checkType}</span>
              {/*<span>חלל: {item.spaceType}</span>*/}
              {checklist?.contractorOnSpacesChecklists?.[0] && (
                <span>קבלן: {getContractorNameFromChecklist(checklist)}</span>
              )}
              {hasAttachments && (
                <span className="flex items-center gap-1">
                  <Paperclip className="w-3 h-3" />
                  {item.attachments.length} קבצים
                </span>
              )}
              {hasPushQueue && (
                <span>לוג לסעיף: {item.pushQueueItems.length}</span>
              )}
            </div>

            {/* Latest Push Queue Item Display */}
            {latestPushItem && (
              <div className="mt-3 p-3 bg-white bg-opacity-50 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge className={getPushQueueStatusColor(latestPushItem)}>
                      {getPushQueueStatusText(latestPushItem)}
                    </Badge>
                    <span className="text-xs text-gray-600">
                      {getChangeTypeText(latestPushItem.changeType)}
                    </span>
                    {Object.keys(users).length > 0 && (
                      <span className="text-xs text-gray-600">
                        • {getUserFullName(latestPushItem.userId, users)}
                      </span>
                    )}
                    <Badge variant="outline" className="text-xs">
                      אחרון
                    </Badge>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(latestPushItem.updatedAt).toLocaleDateString(
                      "he-IL",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </span>
                </div>

                {/* Approval Status Details */}
                {(latestPushItem.approve || latestPushItem.reject) && (
                  <div className="mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      {latestPushItem.approve && (
                        <span className="text-green-600 font-medium">
                          ✓ מאושר
                        </span>
                      )}
                      {latestPushItem.reject && (
                        <span className="text-red-600 font-medium">✗ נדחה</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Remarks for latest item */}
                {latestPushItem.remark && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                    <span className="font-medium text-blue-800">הערה: </span>
                    <span className="text-blue-700">
                      {latestPushItem.remark}
                    </span>
                  </div>
                )}

                {latestPushItem.rejectRemark && (
                  <div className="mt-2 p-2 bg-red-100 rounded text-sm">
                    <span className="font-medium text-red-800">
                      סיבת דחיה:{" "}
                    </span>
                    <span className="text-red-700">
                      {latestPushItem.rejectRemark}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Expanded Content - Attachments and Push Queue History */}
            {(hasAttachments ||
              (hasPushQueue && item.pushQueueItems.length > 1)) &&
              isExpanded && (
                <div className="mt-4 space-y-4 border-t pt-4">
                  {/* Attachments Section */}
                  {hasAttachments && (
                    <div className="space-y-3">
                      <h6 className="text-sm font-medium text-gray-700 text-right">
                        קבצים מצורפים ({item.attachments.length}):
                      </h6>
                      {item.attachments.map((attachment) => (
                        <AttachmentViewer
                          key={attachment.id}
                          attachment={attachment}
                          onImageMagnify={onImageMagnify}
                        />
                      ))}
                    </div>
                  )}

                  {/* Push Queue History Section (excluding latest item) */}
                  {hasPushQueue && item.pushQueueItems.length > 1 && (
                    <div className="space-y-3">
                      <div
                        className="flex items-center justify-between cursor-pointer p-2 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                        onClick={() =>
                          setIsLogHistoryExpanded(!isLogHistoryExpanded)
                        }
                      >
                        <h6 className="text-sm font-medium text-gray-700 text-right">
                          היסטוריית לוג ({item.pushQueueItems.length - 1})
                        </h6>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                        >
                          {isLogHistoryExpanded ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                      {isLogHistoryExpanded && (
                        <div className="space-y-2">
                          {item.pushQueueItems
                            .sort(
                              (a, b) =>
                                new Date(b.updatedAt) - new Date(a.updatedAt),
                            )
                            .slice(1) // Skip the first (latest) item as it's shown in header
                            .map((pushItem) => (
                              <div
                                key={pushItem.id}
                                className="p-3 rounded-lg border bg-gray-50"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge
                                      className={getPushQueueStatusColor(
                                        pushItem,
                                      )}
                                    >
                                      {getPushQueueStatusText(pushItem)}
                                    </Badge>
                                    <span className="text-xs text-gray-600">
                                      {getChangeTypeText(pushItem.changeType)}
                                    </span>
                                    {Object.keys(users).length > 0 && (
                                      <span className="text-xs text-gray-600">
                                        •{" "}
                                        {getUserFullName(
                                          pushItem.userId,
                                          users,
                                        )}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {new Date(
                                      pushItem.updatedAt,
                                    ).toLocaleDateString("he-IL", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </span>
                                </div>

                                {/* Approval Status Details */}
                                {(pushItem.approve || pushItem.reject) && (
                                  <div className="mb-2">
                                    <div className="flex items-center gap-2 text-sm">
                                      {pushItem.approve && (
                                        <span className="text-green-600 font-medium">
                                          ✓ מאושר
                                        </span>
                                      )}
                                      {pushItem.reject && (
                                        <span className="text-red-600 font-medium">
                                          ✗ נדחה
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Remarks */}
                                {pushItem.remark && (
                                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                                    <span className="font-medium text-blue-800">
                                      הערה:{" "}
                                    </span>
                                    <span className="text-blue-700">
                                      {pushItem.remark}
                                    </span>
                                  </div>
                                )}

                                {pushItem.rejectRemark && (
                                  <div className="mt-2 p-2 bg-red-50 rounded text-sm">
                                    <span className="font-medium text-red-800">
                                      סיבת דחיה:{" "}
                                    </span>
                                    <span className="text-red-700">
                                      {pushItem.rejectRemark}
                                    </span>
                                  </div>
                                )}

                                {/* Priority and Attempt Info */}
                                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                  <span>עדיפות: {pushItem.priority}</span>
                                  <span>ניסיונות: {pushItem.attemptCount}</span>
                                  {pushItem.errorMessage && (
                                    <span className="text-red-500">
                                      שגיאה: {pushItem.errorMessage}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
          </div>
          <div className="flex items-center gap-2 ml-4">
            {hasAttachments && !isExpanded && (
              <Badge
                variant="outline"
                className="text-xs flex items-center gap-1"
              >
                <Paperclip className="w-3 h-3" />
                {item.attachments.length}
              </Badge>
            )}
            {hasPushQueue && item.pushQueueItems.length > 1 && !isExpanded && (
              <Badge variant="outline" className="text-xs">
                +{item.pushQueueItems.length - 1} קודמים
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Improved Image Magnify Modal Component
const ImageMagnifyModal = ({ imageUrl, attachment, onClose, isOpen }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen]);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.5, 0.25));
  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "+" || e.key === "=") {
        handleZoomIn();
      } else if (e.key === "-") {
        handleZoomOut();
      } else if (e.key === "0") {
        handleReset();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-95 z-[9999] flex flex-col rtl"
      dir="rtl"
    >
      {/* Header Bar */}
      <div className="bg-black bg-opacity-80 p-4 flex justify-between items-center border-b border-gray-600">
        <div className="text-white text-right">
          <p className="text-sm font-medium">
            {attachment?.attachmentType} •{" "}
            {new Date(attachment?.captureDate).toLocaleDateString("he-IL")}
          </p>
          <p className="text-xs opacity-75">
            הגדלה: {scale.toFixed(1)}x • ESC לסגירה • +/- להגדלה/הקטנה • 0
            לאיפוס
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleZoomOut}
            disabled={scale <= 0.25}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={handleReset}>
            איפוס
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleZoomIn}
            disabled={scale >= 4}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="default" size="sm" onClick={onClose}>
            חזור
          </Button>
        </div>
      </div>

      {/* Image Container */}
      <div
        className="flex-1 overflow-hidden relative bg-black"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="w-full h-full flex items-center justify-center">
          <img
            src={imageUrl}
            alt="מוגדל"
            className="transition-transform duration-200 select-none"
            style={{
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
              cursor:
                scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              maxHeight: scale === 1 ? "90%" : "none",
              maxWidth: scale === 1 ? "90%" : "none",
            }}
            draggable={false}
          />
        </div>
      </div>

      {/* Footer Instructions */}
      <div className="bg-black bg-opacity-80 p-2 text-center border-t border-gray-600">
        <p className="text-white text-xs">
          לחץ וגרור לתזוזה • השתמש בכפתורי הזום או מקלדת • ESC לסגירה
        </p>
      </div>
    </div>
  );
};

const SpaceModal = ({
  isOpen,
  onClose,
  spaceId,
  projectId,
  clientId,
  project,
  mode = 'default', // Add mode prop similar to TourViewer
}) => {
  const [space, setSpace] = useState(null);
  const [tours, setTours] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [users, setUsers] = useState({});
  const [selectedTour, setSelectedTour] = useState(null);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [showTourViewer, setShowTourViewer] = useState(false);
  const [showChecklistDetails, setShowChecklistDetails] = useState(false);
  const [magnifiedImage, setMagnifiedImage] = useState(null);

  // Ensure spaceId is always an integer
  const parsedSpaceId = typeof spaceId === 'string' ? parseInt(spaceId, 10) : spaceId;

  // Fetch space details
  useEffect(() => {
    if (isOpen && parsedSpaceId) {
      fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: "space.findUnique",
          payload: parsedSpaceId, // The prismaService expects just the ID as integer
        },
        label: "space details",
      }).then((fetchedSpace) => {
        if (mode === 'public') {
          // In public mode, verify that the space can be viewed publicly
          // You might want to add an isActivePublic field to the space model
          // For now, we'll allow all spaces to be viewed publicly
          // but you can add your own logic here
          console.log('Public space access for space:', fetchedSpace);
        }
        setSpace(fetchedSpace);
      });
    }
  }, [isOpen, parsedSpaceId, mode]);

  // Fetch tours for this space
  useEffect(() => {
    if (isOpen && parsedSpaceId && (mode === 'public' || (projectId && clientId))) {
      // In public mode, get projectId from the fetched space
      const effectiveProjectId = mode === 'public' ? space?.projectId : projectId;
      
      // In public mode, wait for space to be loaded to get projectId
      if (mode === 'public' && !effectiveProjectId) {
        return;
      }

      fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: "tour.findMany",
          payload: {
            data: { spaceId: parsedSpaceId, projectId: effectiveProjectId },
            include: {
              images: true,
              contractors: {
                include: {
                  contractor: true,
                },
              },
            },
          },
        },
        label: "space tours",
      }).then(async (fetchedTours) => {
        setTours(fetchedTours);

        // Extract unique user IDs from tour creators
        const tourUserIds = new Set();
        fetchedTours.forEach((tour) => {
          if (tour.createdBy) {
            tourUserIds.add(tour.createdBy);
          }
        });

        // Batch fetch tour creator users if we have userIds
        if (tourUserIds.size > 0) {
          try {
            const userPromises = Array.from(tourUserIds).map((userId) =>
              fetchWithRetry({
                url: `${API_BASE_URL}/api/post`,
                data: {
                  function_name: FUNCTION_NAMES.PRISMA,
                  operation: "user.findUnique",
                  payload: userId,
                },
                label: `tour creator ${userId}`,
              }),
            );

            const fetchedTourUsers = await Promise.all(userPromises);
            const tourUsersMap = {};
            fetchedTourUsers.forEach((user) => {
              if (user && user.id) {
                tourUsersMap[user.id] = user;
              }
            });

            // Merge with existing users
            setUsers((prevUsers) => ({ ...prevUsers, ...tourUsersMap }));
          } catch (error) {
            console.error("Error fetching tour creator users:", error);
          }
        }
      });
    }
  }, [isOpen, parsedSpaceId, projectId, clientId, mode, space]);

  // Fetch checklists for this space
  useEffect(() => {
    if (isOpen && parsedSpaceId) {
      fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: "spacesChecklist.findMany",
          payload: {
            where: { spaceId: parsedSpaceId },
            include: {
              checklist: true,
              contractorOnSpacesChecklists: {
                include: {
                  contractor: true,
                },
              },
              spacesChecklistItems: {
                include: {
                  ChecklistItem: true,
                  attachments: true,
                  pushQueueItems: true,
                },
              },
            },
          },
        },
        label: "space checklists",
      }).then(async (fetchedChecklists) => {
        setChecklists(fetchedChecklists);

        // Extract unique user IDs from pushQueueItems and spacesChecklist userCreated
        const userIds = new Set();
        fetchedChecklists.forEach((checklist) => {
          // Add userCreated from SpacesChecklist
          if (checklist.userCreated) {
            userIds.add(checklist.userCreated);
          }

          checklist.spacesChecklistItems?.forEach((item) => {
            item.pushQueueItems?.forEach((pushItem) => {
              if (pushItem.userId) {
                userIds.add(pushItem.userId);
              }
            });
          });
        });

        // Batch fetch users if we have userIds
        if (userIds.size > 0) {
          try {
            const userPromises = Array.from(userIds).map((userId) =>
              fetchWithRetry({
                url: `${API_BASE_URL}/api/post`,
                data: {
                  function_name: FUNCTION_NAMES.PRISMA,
                  operation: "user.findUnique",
                  payload: userId,
                },
                label: `user details ${userId}`,
              }),
            );

            const fetchedUsers = await Promise.all(userPromises);
            const usersMap = {};
            fetchedUsers.forEach((user) => {
              if (user && user.id) {
                usersMap[user.id] = user;
              }
            });

            // Merge with existing users (from tours)
            setUsers((prevUsers) => ({ ...prevUsers, ...usersMap }));
          } catch (error) {
            console.error("Error fetching users:", error);
          }
        }
      });
    }
  }, [isOpen, parsedSpaceId]);

  const handleClose = () => {
    setShowTourViewer(false);
    setShowChecklistDetails(false);
    setSelectedTour(null);
    setSelectedChecklist(null);
    setMagnifiedImage(null);
    onClose();
  };

  const handleImageMagnify = (imageUrl, attachment) => {
    setMagnifiedImage({ imageUrl, attachment });
  };

  const handleCloseMagnify = () => {
    setMagnifiedImage(null);
  };

  const handleTourClick = (tour) => {
    setSelectedTour(tour);
    setShowTourViewer(true);
  };

  const handleChecklistClick = (checklist) => {
    setSelectedChecklist(checklist);
    setShowChecklistDetails(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("he-IL", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTourTypeHebrew = (tourType) => {
    const typeMap = {
      TRUM_TICHNON: "טרום תכנון",
      TICHNON_VS_BITZUA: "תכנון מול ביצוע",
      TIUD_MESIRA: "תיעוד מסירה",
    };
    return typeMap[tourType] || tourType;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      "לא התחיל": "bg-gray-100 text-gray-800",
      בתהליך: "bg-blue-100 text-blue-800",
      הושלם: "bg-green-100 text-green-800",
      נדחה: "bg-red-100 text-red-800",
    };
    return statusColors[status] || "bg-gray-100 text-gray-800";
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          className="max-w-[90vw] max-h-[90vh] w-[90vw] h-[90vh] overflow-hidden p-0 rtl"
          dir="rtl"
        >
          {/* Header with close button */}
          <div className="flex items-center justify-between p-6 border-b bg-gray-50">
            <div className="flex items-center gap-4 flex-row-reverse">
              {/* In public mode, use project data from space, otherwise use prop */}
              {((mode === 'public' ? space?.project?.imageUrl : project?.imageUrl)) && (
                <img
                  src={mode === 'public' ? space?.project?.imageUrl : project?.imageUrl}
                  alt={mode === 'public' ? space?.project?.name : project?.name}
                  className="w-12 h-12 object-cover rounded-lg"
                />
              )}
              <div>
                <h2 className="text-2xl font-bold">
                  {mode === 'public' ? space?.project?.name : project?.name}
                </h2>
                {space?.id && (
                  <span className="text-sm text-gray-500">ID: {space.id}</span>
                )}
                {mode === 'public' && (
                  <span className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                    🌐 Public View
                  </span>
                )}
                {/*<p className="text-gray-600 flex items-center gap-2 text-right">*/}
                {/*  <Building className="w-4 h-4" />*/}
                {/*  {space?.name} | {space?.lot?.name} | {space?.building?.name} |{" "}*/}
                {/*  {space?.floor?.name}*/}
                {/*</p>*/}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-hidden">
            {showTourViewer && selectedTour ? (
              <div className="h-full">
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">
                        {selectedTour.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {getTourTypeHebrew(selectedTour.tourType)} | נוצר:{" "}
                        {formatDate(selectedTour.createdAt)}
                        {selectedTour.createdBy && (
                          <span>
                            {" "}
                            על ידי{" "}
                            {getUserFullName(selectedTour.createdBy, users)}
                          </span>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setShowTourViewer(false)}
                    >
                      חזור לסיורים
                    </Button>
                  </div>
                </div>
                <div className="h-[calc(100%-80px)]">
                  <TourViewer mode="embedded" preselectedTour={selectedTour} />
                </div>
              </div>
            ) : showChecklistDetails && selectedChecklist ? (
              <div className="p-6 h-full overflow-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold">
                      {selectedChecklist.checklist?.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedChecklist.checklist?.description}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowChecklistDetails(false)}
                  >
                    חזור לצ'קליסטים
                  </Button>
                </div>

                {/* Checklist Status */}
                <Card className="mb-6">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">סטטוס</p>
                        <Badge
                          className={getStatusColor(selectedChecklist.status)}
                        >
                          {selectedChecklist.status}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">אחוזי השלמה</p>
                        <p className="font-semibold">
                          {selectedChecklist.totalPercentage.toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">דחיות</p>
                        <p className="font-semibold text-red-600">
                          {selectedChecklist.rejectCount}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">נוצר על ידי</p>
                        <p className="text-sm font-medium">
                          {getUserFullName(
                            selectedChecklist.userCreated,
                            users,
                          )}
                        </p>
                      </div>
                      {selectedChecklist?.contractorOnSpacesChecklists?.[0] && (
                        <div>
                          <p className="text-sm text-gray-600">קבלן</p>
                          <p className="text-sm font-medium">
                            {getContractorNameFromChecklist(selectedChecklist)}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-600">עודכן לאחרונה</p>
                        <p className="text-sm">
                          {formatDate(selectedChecklist.updatedAt)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Checklist Items */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-right">
                    פריטי צ'קליסט
                  </h4>
                  {selectedChecklist.spacesChecklistItems?.map((item) => (
                    <ExpandableChecklistItem
                      key={item.id}
                      item={item}
                      onImageMagnify={handleImageMagnify}
                      users={users}
                      checklist={selectedChecklist}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-6 h-full overflow-auto">
                {/* Space Details */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-right">
                      <MapPin className="w-5 h-5" />
                      פרטי חלל
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">שם החלל</p>
                        <p className="font-semibold">{space?.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">הערות</p>
                        <p className="text-sm">{space?.notes || "אין הערות"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">מיקום</p>
                        <p className="text-sm">
                          {space?.lot?.name} | {space?.building?.name} |{" "}
                          {space?.floor?.name}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Tours Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-right">
                        <Eye className="w-5 h-5" />
                        סיורים ({tours.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {tours.length === 0 ? (
                        <p className="text-center text-gray-500 py-4 text-right">
                          אין סיורים זמינים עבור חלל זה
                        </p>
                      ) : (
                        tours.map((tour) => (
                          <Card
                            key={tour.id}
                            className="border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer"
                            onClick={() => handleTourClick(tour)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h5 className="font-medium">{tour.name}</h5>
                                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {formatDate(tour.createdAt)}
                                    </span>
                                    {tour.createdBy && (
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        {getUserFullName(tour.createdBy, users)}
                                      </span>
                                    )}
                                    {getContractorNameFromTour(tour) && (
                                      <span className="flex items-center gap-1 text-blue-600">
                                        קבלן: {getContractorNameFromTour(tour)}
                                      </span>
                                    )}
                                    {tour.tourType && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {getTourTypeHebrew(tour.tourType)}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {tour.isActivePublic && (
                                    <Badge className="bg-green-100 text-green-800 text-xs">
                                      פעיל
                                    </Badge>
                                  )}
                                  <Eye className="w-4 h-4 text-blue-600" />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </CardContent>
                  </Card>

                  {/* Checklists Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-right">
                        <FileText className="w-5 h-5" />
                        צ'קליסטים ({checklists.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {checklists.length === 0 ? (
                        <p className="text-center text-gray-500 py-4 text-right">
                          אין צ'קליסטים זמינים עבור חלל זה
                        </p>
                      ) : (
                        checklists.map((checklist) => (
                          <Card
                            key={checklist.id}
                            className="border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer"
                            onClick={() => handleChecklistClick(checklist)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h5 className="font-medium">
                                    {checklist.checklist?.name}
                                  </h5>
                                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {formatDate(checklist.updatedAt)}
                                    </span>
                                    {checklist.userCreated && (
                                      <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        נוצר על ידי{" "}
                                        {getUserFullName(
                                          checklist.userCreated,
                                          users,
                                        )}
                                      </span>
                                    )}
                                    {getContractorNameFromChecklist(checklist) && (
                                      <span className="flex items-center gap-1 text-blue-600">
                                        קבלן: {getContractorNameFromChecklist(checklist)}
                                      </span>
                                    )}
                                    <span>
                                      {checklist.totalPercentage.toFixed(1)}%
                                      הושלם
                                    </span>
                                    {checklist.rejectCount > 0 && (
                                      <span className="text-red-600">
                                        {checklist.rejectCount} דחיות
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    className={getStatusColor(checklist.status)}
                                  >
                                    {checklist.status}
                                  </Badge>
                                  <FileText className="w-4 h-4 text-blue-600" />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Magnify Modal */}
      <ImageMagnifyModal
        imageUrl={magnifiedImage?.imageUrl}
        attachment={magnifiedImage?.attachment}
        onClose={handleCloseMagnify}
        isOpen={!!magnifiedImage}
      />
    </>
  );
};

export default SpaceModal;
